import { IChartContainer } from "../models/base/chart-container.interface";
import { ChartStyleSettingsModel } from "../models/chart-style/chart-style-settings.model";
import { TradeOrderDataModel } from "../models/draw-trade-order/trade-order-data.model";
import { IComponent } from "./component.interface";
import { PnlComponent } from "./pnl.component";
import { TradePlotLineComponent } from "./trade-plot-line.component";
import { OrderBracketManagerComponent } from "./order-bracket-manager.component";

interface PendingMoveConfirmation {
    accountName: string;
    shares: number;
    isLong: boolean;
    tradeType: string;
    originalPrice: number;
    ts: number;
}

export class DrawTradeOrdersComponent implements IComponent {
    public id = '';

    public tradePlotLines: Array<TradePlotLineComponent> = [];
    public tradeOrders: Array<TradeOrderDataModel> = [];
    public removeTradeIds: Array<string> = [];
    private pendingMoveConfirmations: Array<PendingMoveConfirmation> = [];
    private base: IChartContainer;
    private isRemovingForSwitchingSymbol = false;
    private canDraw = false;
    public initialize = false;

    constructor(base: IChartContainer) {
        this.base = base;

        if (!this.base.marketingWebChartMode) {
            this.canDraw = true;
        }

        this.subscribeDrawTradeOrders();
        this.subscribeChartEvents();
    }

    public hasAnyTradeOrders(): boolean {
        return this.tradeOrders.length > 0;
    }

    public getOriginalOrderPrice(id: string): number | null {
        return this.originalTradeOrders.find(o => o.id === id)?.price ?? null;
    }

    public remove() {
        this.isRemovingForSwitchingSymbol = true;
        if (this.base.marketingWebChartMode) {
            this.canDraw = false;
        }

        for (let i = 0; i < this.tradePlotLines.length; i++) {
            this.tradePlotLines[i].remove();
            this.tradePlotLines[i] = null;
        }

        this.tradeOrders = [];
        this.originalTradeOrders = [];
        this.allTradeOrders = [];
        this.tradePlotLines = [];
        this.removeTradeIds = [];
        this.initialize = false;

        this.isRemovingForSwitchingSymbol = false;
    }

    public enable() {
        if (this.canDraw) return;

        this.canDraw = true;
        this.updateTrades();
    }

    public disable() {
        this.canDraw = false;
        for (let i = 0; i < this.tradePlotLines.length; i++) {
            this.tradePlotLines[i].remove();
            this.tradePlotLines[i] = null;
        }

        this.tradePlotLines = [];

    }

    public get elementMaximumWidth(): number {
        let width = 0;
        if (this.tradePlotLines?.length) {
            for (let tradeOrderLine of this.tradePlotLines) {
                if (tradeOrderLine.elementWidth > width) {
                    width = tradeOrderLine.elementWidth;
                }
            }
        }

        return width;
    }

    private subscribeChartEvents() {
        this.base.tiChartSeries?.subscribeExtremeChange(this.id, () => {
            for (const tradeOrder of this.tradePlotLines) {
                tradeOrder.onExtreamChange();
            }
        });
        this.base.tiChartSeries?.subscribeRedrawChange(this.id, () => {
            for (const tradeOrder of this.tradePlotLines) {
                tradeOrder.onRedraw();
            }
        });
    }

    private drawTradeOrdersTimer: any;
    private chartStyle: ChartStyleSettingsModel | null = null;
    private originalTradeOrders: Array<TradeOrderDataModel> = [];
    public allTradeOrders: Array<TradeOrderDataModel> = [];
    private subscribeDrawTradeOrders() {
        this.base.chartService?.subscribeChartStyleData((chartStyle: ChartStyleSettingsModel) => {
            this.chartStyle = chartStyle;
            this.populateTradeOrders();
        });
        this.base.chartService.subscribeDrawTradeOrdersData((tradeOrders: Array<TradeOrderDataModel>) => {
            this.originalTradeOrders = tradeOrders;
            this.populateTradeOrders();
        });
        this.base.chartService.subscribeAllTradeOrdersData((allTradeOrders: Array<TradeOrderDataModel>) => {
            this.allTradeOrders = allTradeOrders;
            this.populateAllTradeOrders();
        });
        this.base.chartService.subscribe_CancelExecuteTradeImmediately(() => {
            for (const tradeOrder of this.tradePlotLines) {
                tradeOrder.cancelExecuteTradeImmediately();
            }
        });
    }

    private populateAllTradeOrders() {
        const nonChartMode = this.chartStyle?.nonNormalChartMode();
        if (this.isRemovingForSwitchingSymbol || nonChartMode) {
            return;
        }

        for (const tradeOrder of this.tradePlotLines) {
            tradeOrder.updateAllTradeOrders(this.allTradeOrders);
        }
    }

    private previousTradeOrders: Array<TradeOrderDataModel> = [];
    private populateTradeOrders() {
        const nonChartMode = this.chartStyle?.nonNormalChartMode();
        if (this.isRemovingForSwitchingSymbol || nonChartMode) {
            return;
        }

        // Find orders that previously allowed moving but now don't
        const previouslyMovableNowFixed = this.findPreviouslyMovableOrders();

        this.tradeOrders = [];
        for (const t of this.originalTradeOrders) {

            if (this.chartStyle?.chartSettingsPopupModel?.defaultEnableTradeOrderEntry && !!t.ocaGroup) {
                continue;
            }

            if (t.tradeType?.toLowerCase() == 'market' || !t.allowMoving || this.removeTradeIds.includes(t.id)) {
                continue;
            }

            this.tradeOrders.push(t);
        }

        // Update previous state for next comparison
        this.previousTradeOrders = [...this.originalTradeOrders];

        if (previouslyMovableNowFixed?.[0]) {
            this.setPnlLastTradeAccount(previouslyMovableNowFixed[0].accountName);
        }

        this.updateTrades();

        if (!this.initialize) {
            setTimeout(() => {
                this.initialize = true;
            }, 500);
        }
    }

    private findPreviouslyMovableOrders(): Array<TradeOrderDataModel> {
        const result: Array<TradeOrderDataModel> = [];

        for (const currentOrder of this.originalTradeOrders) {
            // Find the same order in previous state
            const previousOrder = this.previousTradeOrders.find(prev => prev.id === currentOrder.id);

            if (previousOrder &&
                previousOrder.allowMoving === true &&
                currentOrder.allowMoving === false) {
                result.push(currentOrder);
            }
        }

        return result;
    }

    private updateTrades() {
        const nonChartMode = this.chartStyle?.nonNormalChartMode();
        if (!this.canDraw || nonChartMode) return;

        if (this.base.marketingWebChartMode) {
            for (const tradeOrder of this.tradeOrders) {
                const id = tradeOrder.getClassName();
                const existPlotLine = this.tradePlotLines.find((x: TradePlotLineComponent) => x.id == id);
                if (existPlotLine && existPlotLine.model && !existPlotLine.moveMode && !existPlotLine.selected) {
                    existPlotLine.model.price = tradeOrder.price;
                    existPlotLine.model.limitPrice = tradeOrder.limitPrice;
                    existPlotLine.model.stopPrice = tradeOrder.stopPrice;

                    if (existPlotLine.model.allowMoving != tradeOrder.allowMoving) {
                        existPlotLine.update(tradeOrder);
                    }
                }
            }
        }

        // Remove cancelled lines before the hover/move guards so cancels always show immediately.
        this.removeGonePlotLines();

        const anyPlotLineMoving = this.tradePlotLines.some((x: TradePlotLineComponent) => x.isMoving());
        if (anyPlotLineMoving) {
            return;
        }

        // Skip full redraw on hover unless orders actually changed (prevents deferring updates until mouse leaves).
        const anyHovering = this.tradePlotLines.some((x: TradePlotLineComponent) => x.mouseHover);
        if (anyHovering) {
            const ordersChanged = this.computeOrdersSignature() !== this.lastOrdersSignature;
            if (!ordersChanged) {
                return;
            }
        }

        clearTimeout(this.drawTradeOrdersTimer);
        this.drawTradeOrdersTimer = setTimeout(() => {
            this.removeTradePlotLinesAfterUpdate();
            this.createTradePlotLines();
            this.lastOrdersSignature = this.computeOrdersSignature();
        }, 100);
    }

    // Hash of drawable orders; used by the hover guard to detect real data changes.
    private lastOrdersSignature = '';
    private computeOrdersSignature(): string {
        return this.tradeOrders
            .map(o => `${o.getClassName()}:${o.price}:${o.stopPrice}:${o.limitPrice}:${o.allowMoving}:${o.shares}`)
            .sort()
            .join('|');
    }

    // Remove lines for orders that no longer exist, but leave pending-move lines alone.
    private removeGonePlotLines() {
        const remaining: TradePlotLineComponent[] = [];
        for (const plotLine of this.tradePlotLines) {
            const stillExists = this.tradeOrders.some(o => o.getClassName() === plotLine.id);
            if (!stillExists && !plotLine.isPending) {
                plotLine.remove();
            } else {
                remaining.push(plotLine);
            }
        }
        this.tradePlotLines = remaining;
    }

    private removeTradePlotLinesAfterUpdate() {
        const remainingPlotLines: TradePlotLineComponent[] = [];

        for (let i = 0; i < this.tradePlotLines.length; i++) {
            const plotLine = this.tradePlotLines[i];

            if (plotLine.isPending) {
                const currentOrder = this.tradeOrders.find(o => o.getClassName() === plotLine.id);
                if (currentOrder && currentOrder.price === plotLine.pendingOriginalPrice) {
                    remainingPlotLines.push(plotLine);
                    plotLine.debouncePendingRemove();
                    continue;
                }
            }

            plotLine.remove();
        }

        this.tradePlotLines = remainingPlotLines;
    }

    private createTradePlotLines() {
        for (const tradeOrder of this.tradeOrders) {
            const id = tradeOrder.getClassName();
            if (this.tradePlotLines.find(p => p.id === id)) {
                continue;
            }
            const line = new TradePlotLineComponent(this.base, tradeOrder, this);
            this.tradePlotLines.push(line);
            this.tryConfirmPendingMove(line, tradeOrder);
        }
    }

    public registerPendingMove(order: TradeOrderDataModel, originalPrice: number) {
        const now = Date.now();
        this.pendingMoveConfirmations = this.pendingMoveConfirmations.filter(m => now - m.ts < 30000);
        this.pendingMoveConfirmations.push({
            accountName: order.accountName,
            shares: order.shares,
            isLong: order.isLong,
            tradeType: order.tradeType?.toLowerCase() ?? '',
            originalPrice,
            ts: now,
        });
    }

    private tryConfirmPendingMove(line: TradePlotLineComponent, order: TradeOrderDataModel) {
        if (this.pendingMoveConfirmations.length === 0) return;

        const now = Date.now();
        const orderType = order.tradeType?.toLowerCase() ?? '';
        const idx = this.pendingMoveConfirmations.findIndex(m =>
            now - m.ts < 30000 &&
            m.accountName === order.accountName &&
            m.shares === order.shares &&
            m.isLong === order.isLong &&
            m.tradeType === orderType &&
            order.price !== m.originalPrice
        );

        if (idx === -1) return;

        this.pendingMoveConfirmations.splice(idx, 1);

        const orderBracketManager = this.base.getComponentsByType(OrderBracketManagerComponent)?.[0] as OrderBracketManagerComponent;
        if (orderBracketManager) {
            orderBracketManager.selectedGroupId = order.id;
        }
        line.enable();
    }

    private setPnlLastTradeAccount(account: string) {
        const pnl = this.base.getComponentsByType(PnlComponent)[0] as PnlComponent;
        if (pnl && !pnl.selectedAccount) {
            pnl.lastTradeAccount = account;
            pnl.setSelectPositionByAccount(account);
        }
    }
}